import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface StatusOption {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface StatusBadgeProps {
  currentStatus: string;
  options: StatusOption[];
  onStatusChange: (newStatus: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const statusColors = {
  // Candidate statuses (capitalized)
  'Onboarding': { color: 'text-blue-700', bgColor: 'bg-blue-100', border: 'border-blue-200' },
  'Interviewing': { color: 'text-yellow-700', bgColor: 'bg-yellow-100', border: 'border-yellow-200' },
  'Offer': { color: 'text-purple-700', bgColor: 'bg-purple-100', border: 'border-purple-200' },
  'Hired': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
  'Rejected': { color: 'text-red-700', bgColor: 'bg-red-100', border: 'border-red-200' },
  
  // Legacy lowercase candidate statuses (for backwards compatibility)
  'onboarding': { color: 'text-blue-700', bgColor: 'bg-blue-100', border: 'border-blue-200' },
  'interviewing': { color: 'text-yellow-700', bgColor: 'bg-yellow-100', border: 'border-yellow-200' },
  'awaiting-ticket': { color: 'text-purple-700', bgColor: 'bg-purple-100', border: 'border-purple-200' },
  'awaiting-visa': { color: 'text-orange-700', bgColor: 'bg-orange-100', border: 'border-orange-200' },
  'hired': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
  'rejected': { color: 'text-red-700', bgColor: 'bg-red-100', border: 'border-red-200' },
  'cancelled': { color: 'text-gray-700', bgColor: 'bg-gray-100', border: 'border-gray-200' },
  
  // Task statuses
  'pending': { color: 'text-yellow-700', bgColor: 'bg-yellow-100', border: 'border-yellow-200' },
  'in-progress': { color: 'text-blue-700', bgColor: 'bg-blue-100', border: 'border-blue-200' },
  'completed': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
  'overdue': { color: 'text-red-700', bgColor: 'bg-red-100', border: 'border-red-200' },
  
  // Appointment statuses
  'scheduled': { color: 'text-blue-700', bgColor: 'bg-blue-100', border: 'border-blue-200' },
  'confirmed': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
  'no-show': { color: 'text-orange-700', bgColor: 'bg-orange-100', border: 'border-orange-200' },
  'rescheduled': { color: 'text-purple-700', bgColor: 'bg-purple-100', border: 'border-purple-200' },
  'deployed': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
  'on-hold': { color: 'text-gray-700', bgColor: 'bg-gray-100', border: 'border-gray-200' },
  
  // Job statuses
  'active': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
  'closed': { color: 'text-gray-700', bgColor: 'bg-gray-100', border: 'border-gray-200' },
  'draft': { color: 'text-yellow-700', bgColor: 'bg-yellow-100', border: 'border-yellow-200' },
  
  // Priority levels
  'high': { color: 'text-red-700', bgColor: 'bg-red-100', border: 'border-red-200' },
  'medium': { color: 'text-yellow-700', bgColor: 'bg-yellow-100', border: 'border-yellow-200' },
  'low': { color: 'text-green-700', bgColor: 'bg-green-100', border: 'border-green-200' },
};

const sizeClasses = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-2 text-base'
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  currentStatus,
  options,
  onStatusChange,
  disabled = false,
  size = 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentOption = options.find(option => option.value === currentStatus);
  const statusStyle = statusColors[currentStatus as keyof typeof statusColors] || statusColors.pending;
  
  if (disabled) {
    return (
      <span className={`inline-flex items-center ${sizeClasses[size]} font-medium rounded-full border ${statusStyle.color} ${statusStyle.bgColor} ${statusStyle.border}`}>
        {currentOption?.label || currentStatus}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center ${sizeClasses[size]} font-medium rounded-full border ${statusStyle.color} ${statusStyle.bgColor} ${statusStyle.border} hover:opacity-80 transition-opacity cursor-pointer gap-1`}
      >
        {currentOption?.label || currentStatus}
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-20">
            {options.map((option) => {
              const optionStyle = statusColors[option.value as keyof typeof statusColors] || statusColors.pending;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onStatusChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                    option.value === currentStatus ? 'bg-gray-50' : ''
                  }`}
                >
                  <span className={`w-3 h-3 rounded-full ${optionStyle.bgColor} border ${optionStyle.border}`} />
                  {option.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Predefined status options for different entities
export const candidateStatusOptions: StatusOption[] = [
  { value: 'Onboarding', label: 'Onboarding', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  { value: 'Interviewing', label: 'Interviewing', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { value: 'Offer', label: 'Offer', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  { value: 'Hired', label: 'Hired', color: 'text-green-700', bgColor: 'bg-green-100' },
  { value: 'Rejected', label: 'Rejected', color: 'text-red-700', bgColor: 'bg-red-100' }
];

export const taskStatusOptions: StatusOption[] = [
  { value: 'pending', label: 'Pending', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { value: 'in-progress', label: 'In Progress', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  { value: 'completed', label: 'Completed', color: 'text-green-700', bgColor: 'bg-green-100' },
  { value: 'overdue', label: 'Overdue', color: 'text-red-700', bgColor: 'bg-red-100' }
];

export const appointmentStatusOptions: StatusOption[] = [
  { value: 'scheduled', label: 'Scheduled', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  { value: 'completed', label: 'Completed', color: 'text-green-700', bgColor: 'bg-green-100' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-700', bgColor: 'bg-red-100' },
  { value: 'no-show', label: 'No Show', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  { value: 'rescheduled', label: 'Rescheduled', color: 'text-purple-700', bgColor: 'bg-purple-100' }
];

export const candidateStageOptions: StatusOption[] = [
  { value: 'interviewing', label: 'Interviewing', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { value: 'awaiting-visa', label: 'Awaiting Visa', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-700', bgColor: 'bg-red-100' },
  { value: 'deployed', label: 'Deployed', color: 'text-green-700', bgColor: 'bg-green-100' },
  { value: 'on-hold', label: 'On Hold', color: 'text-gray-700', bgColor: 'bg-gray-100' }
];

export const jobStatusOptions: StatusOption[] = [
  { value: 'active', label: 'Active', color: 'text-green-700', bgColor: 'bg-green-100' },
  { value: 'closed', label: 'Closed', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  { value: 'draft', label: 'Draft', color: 'text-yellow-700', bgColor: 'bg-yellow-100' }
];

export const priorityOptions: StatusOption[] = [
  { value: 'high', label: 'High', color: 'text-red-700', bgColor: 'bg-red-100' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { value: 'low', label: 'Low', color: 'text-green-700', bgColor: 'bg-green-100' }
];